import { useState, useEffect, useRef, useCallback } from "react";
import { encodeFunctionData, parseUnits, isAddress } from "viem";
import { useGaslessSendTransaction } from "@/hooks/useAlchemy";
import { offrampApi, OfframpQuote } from "@/api/offramp";
import { useCoinbaseTransactionStatus, CoinbaseTransaction } from "@/hooks/useCoinbaseTransactionStatus";
import { triggerGlobalToast } from "@/contexts/ToastContext";

// Constants
const USDC_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
const DISMISS_THRESHOLD = 150;
const ANIMATION_DURATION = 300;
const MIN_WALLET_AMOUNT = 5;
const MIN_OFFRAMP_AMOUNT = 10;
const MAX_OFFRAMP_AMOUNT = 25000;

const ERC20_TRANSFER_ABI = [
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
] as const;

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  balance: number;
  onSuccess?: () => void;
}

type WithdrawStep = "method-select" | "wallet-transfer" | "offramp";
type WithdrawState = "idle" | "processing" | "success" | "error" | "polling";
type CloseType = "none" | "button" | "drag";

export function WithdrawModal({
  isOpen,
  onClose,
  balance,
  onSuccess,
}: WithdrawModalProps) {
  // Step management
  const [step, setStep] = useState<WithdrawStep>("method-select");

  // Wallet transfer state
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [withdrawState, setWithdrawState] = useState<WithdrawState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Offramp state
  const [offrampAmount, setOfframpAmount] = useState("");
  const [offrampQuote, setOfframpQuote] = useState<OfframpQuote | null>(null);
  const [offrampState, setOfframpState] = useState<WithdrawState>("idle");
  const [offrampError, setOfframpError] = useState("");

  // Polling state for offramp
  const [shouldPollOfframp, setShouldPollOfframp] = useState(false);

  // Transaction polling hook for offramp - callbacks handle success/failure
  useCoinbaseTransactionStatus(
    'offramp',
    shouldPollOfframp,
    {
      pollInterval: 5000,
      maxPollDuration: 5 * 60 * 1000, // 5 minutes
      onSuccess: (tx: CoinbaseTransaction) => {
        const amount = tx.fiatAmount || tx.cryptoAmount || '';
        triggerGlobalToast(
          `Withdrawal complete! $${amount} sent to your bank`,
          'success'
        );
        setShouldPollOfframp(false);
        setOfframpState('idle');
        if (onSuccess) {
          onSuccess();
        }
        handleClose('button');
      },
      onFailed: (tx: CoinbaseTransaction) => {
        const reason = tx.failureReason || 'Please try again';
        triggerGlobalToast(`Withdrawal failed: ${reason}`, 'error');
        setShouldPollOfframp(false);
        setOfframpState('error');
        setOfframpError(reason);
      },
    }
  );

  // Animation state
  const [closeType, setCloseType] = useState<CloseType>("none");
  const [dragY, setDragY] = useState(0);
  const [canDrag, setCanDrag] = useState(false);

  // Refs for synchronous drag tracking
  const dragRef = useRef({ active: false, startY: 0 });

  const { sendTransaction, isLoading } = useGaslessSendTransaction();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep("method-select");
      setToAddress("");
      setAmount("");
      setWithdrawState("idle");
      setErrorMessage("");
      setOfframpAmount("");
      setOfframpQuote(null);
      setOfframpState("idle");
      setOfframpError("");
      setShouldPollOfframp(false);
      setCloseType("none");
      setDragY(0);
      setCanDrag(false);
      dragRef.current = { active: false, startY: 0 };
    } else {
      // Stop polling when modal closes
      setShouldPollOfframp(false);
    }
  }, [isOpen]);

  // Close handler
  const handleClose = useCallback(
    (type: "button" | "drag" = "button") => {
      if (withdrawState === "processing" || offrampState === "processing" || closeType !== "none") return;

      // Stop polling when closing
      setShouldPollOfframp(false);

      setCloseType(type);
      if (type === "drag") {
        setDragY(window.innerHeight);
      }
      setTimeout(onClose, ANIMATION_DURATION);
    },
    [withdrawState, offrampState, closeType, onClose]
  );

  // Cancel polling and reset to allow retry
  const handleCancelOfframpPolling = useCallback(() => {
    setShouldPollOfframp(false);
    setOfframpState("idle");
  }, []);

  // Back handler
  const handleBack = () => {
    setStep("method-select");
    setWithdrawState("idle");
    setOfframpState("idle");
    setErrorMessage("");
    setOfframpError("");
    setOfframpQuote(null);
  };

  // Pointer event handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    if (withdrawState === "processing" || offrampState === "processing" || !canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { active: true, startY: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const newDragY = Math.max(0, e.clientY - dragRef.current.startY);
    setDragY(newDragY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragRef.current.active = false;

    if (dragY > DISMISS_THRESHOLD) {
      handleClose("drag");
    } else {
      setDragY(0);
    }
  };

  if (!isOpen) return null;

  // Derived state
  const isClosing = closeType !== "none";
  const isDragging = dragRef.current.active;
  const numericAmount = parseFloat(amount) || 0;
  const numericOfframpAmount = parseFloat(offrampAmount) || 0;

  const isValidAddress = toAddress && isAddress(toAddress);
  const isValidAmount = numericAmount >= MIN_WALLET_AMOUNT && numericAmount <= balance;
  const canWithdraw = isValidAddress && isValidAmount && withdrawState === "idle";

  const isValidOfframpAmount = numericOfframpAmount >= MIN_OFFRAMP_AMOUNT &&
    numericOfframpAmount <= Math.min(balance, MAX_OFFRAMP_AMOUNT);

  const handleMax = () => setAmount(balance.toFixed(2));
  const handleOfframpMax = () => setOfframpAmount(Math.min(balance, MAX_OFFRAMP_AMOUNT).toFixed(2));

  // Wallet transfer handler
  const handleWithdraw = async () => {
    if (!canWithdraw) return;

    setWithdrawState("processing");
    setErrorMessage("");

    try {
      const data = encodeFunctionData({
        abi: ERC20_TRANSFER_ABI,
        functionName: "transfer",
        args: [toAddress as `0x${string}`, parseUnits(amount, 6)],
      });

      const result = await sendTransaction({
        to: USDC_ADDRESS as `0x${string}`,
        data,
        value: "0x0",
      });

      if (result?.txnHash) {
        setWithdrawState("success");
        onSuccess?.();
        setTimeout(() => handleClose("button"), 2000);
      } else {
        throw new Error("Transaction failed - no hash returned");
      }
    } catch (error: any) {
      console.error("Withdraw failed:", error);
      setWithdrawState("error");
      setErrorMessage(error.shortMessage || error.message || "Withdrawal failed");
    }
  };

  // Offramp handler
  const handleOfframp = async () => {
    if (!isValidOfframpAmount || offrampState === "processing") return;

    setOfframpState("processing");
    setOfframpError("");

    try {
      const result = await offrampApi.createUrl(numericOfframpAmount);
      setOfframpQuote(result.quote);
      setOfframpState("success");

      // Detect mobile/PWA environment
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const isPWA = window.matchMedia("(display-mode: standalone)").matches;

      // On mobile or PWA, use full-page navigation
      if (isMobile || isPWA) {
        window.location.href = result.offrampUrl;
        // Note: For mobile redirect, polling will resume when user returns
      } else {
        // Desktop: Open in centered popup window
        const width = 500;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          result.offrampUrl,
          "coinbase-offramp",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          console.warn("Popup was blocked, falling back to full-page navigation");
          window.location.href = result.offrampUrl;
          return;
        }

        // Start polling for transaction status
        setOfframpState("polling");
        setShouldPollOfframp(true);
      }
    } catch (error: any) {
      console.error("Failed to generate offramp URL:", error);
      setOfframpState("error");
      setOfframpError(
        error.response?.data?.error || "Failed to generate withdrawal link. Please try again."
      );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        style={{
          opacity: dragY > 0 ? Math.max(0, 1 - dragY / 300) : undefined,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose("button");
        }}
      />

      {/* Modal */}
      <div
        className="fixed inset-x-0 bottom-0 z-60 flex justify-center sm:items-center sm:inset-0 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`w-full max-w-[440px] bg-[#102210] sm:rounded-[2.5rem] rounded-t-[2.5rem] relative flex flex-col max-h-[92vh] sm:max-h-[90vh] shadow-2xl ring-1 ring-white/10 overflow-hidden ${
            closeType === "drag"
              ? ""
              : closeType === "button"
              ? "animate-slide-down"
              : canDrag
              ? ""
              : "animate-slide-up"
          }`}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? "none" : "transform 0.3s ease-out",
          }}
          onAnimationEnd={() => {
            if (!isClosing) setCanDrag(true);
          }}
        >
          {/* Drag Handle */}
          <div
            className="flex justify-center pt-6 pb-4 shrink-0 cursor-grab active:cursor-grabbing select-none"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className="h-1.5 w-14 rounded-full bg-white/20 hover:bg-white/30 transition-colors pointer-events-none" />
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 pt-2 pb-8 scrollbar-hide">
            {/* Back button for sub-steps */}
            {step !== "method-select" && (
              <button
                onClick={handleBack}
                className="absolute top-6 left-6 z-20 flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
              >
                <span className="material-symbols-outlined text-xl">arrow_back</span>
              </button>
            )}

            {/* Step 1: Method Selection */}
            {step === "method-select" && (
              <div className="flex flex-col items-center text-center mt-2">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                  <div className="relative w-24 h-24 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10 shadow-[0_8px_16px_-4px_rgba(0,0,0,0.5)]">
                    <span className="text-primary text-[40px] font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      $
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-white tracking-tight text-[28px] font-extrabold leading-tight mb-3">
                  Withdraw Funds
                </h2>

                {/* Balance Badge */}
                <div className="flex items-center gap-2.5 bg-[#224922]/40 px-5 py-2 rounded-full border border-white/5 backdrop-blur-md mb-10">
                  <span className="material-symbols-outlined text-[#90cb90] text-xl">
                    account_balance_wallet
                  </span>
                  <p className="text-[#90cb90] text-sm font-semibold">
                    Available:{" "}
                    <span className="text-white font-bold tracking-wide">
                      ${balance.toFixed(2)}
                    </span>
                  </p>
                </div>

                {/* Method Options */}
                <div className="w-full space-y-4">
                  {/* Option 1: Cash out to Bank */}
                  <button
                    onClick={() => setStep("offramp")}
                    className="w-full group flex items-center text-left p-4 pr-5 rounded-2xl border border-white/10 bg-[#182e18] hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent to-transparent group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                    <div className="flex-shrink-0 mr-4 relative z-10">
                      <div className="w-12 h-12 rounded-xl bg-[#224922] flex items-center justify-center text-primary ring-1 ring-white/5 group-hover:scale-105 transition-transform duration-300">
                        <span className="material-symbols-outlined">account_balance</span>
                      </div>
                    </div>
                    <div className="flex-grow relative z-10">
                      <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">
                        Cash Out to Bank
                      </h3>
                      <p className="text-xs text-[#90cb90]/70 mb-1">
                        Withdraw to your bank account
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-primary/70">
                        Powered by Coinbase
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-2 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all relative z-10">
                      <span className="material-symbols-outlined text-[22px]">arrow_forward</span>
                    </div>
                  </button>

                  {/* Option 2: Transfer to Wallet */}
                  <button
                    onClick={() => setStep("wallet-transfer")}
                    className="w-full group flex items-center text-left p-4 pr-5 rounded-2xl border border-white/10 bg-[#182e18] hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent to-transparent group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                    <div className="flex-shrink-0 mr-4 relative z-10">
                      <div className="w-12 h-12 rounded-xl bg-[#224922] flex items-center justify-center text-primary ring-1 ring-white/5 group-hover:scale-105 transition-transform duration-300">
                        <span className="material-symbols-outlined">send</span>
                      </div>
                    </div>
                    <div className="flex-grow relative z-10">
                      <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">
                        Transfer to Wallet
                      </h3>
                      <p className="text-xs text-[#90cb90]/70 mb-1">
                        Send to another crypto wallet
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-primary/70">
                        No gas fees
                      </p>
                    </div>
                    <div className="flex-shrink-0 ml-2 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all relative z-10">
                      <span className="material-symbols-outlined text-[22px]">arrow_forward</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2a: Offramp (Cash out to Bank) */}
            {step === "offramp" && (
              <div className="flex flex-col items-center text-center mt-8">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                  <div className="relative w-20 h-20 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10">
                    <span className="material-symbols-outlined text-primary text-[36px]">
                      account_balance
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-white tracking-tight text-[24px] font-extrabold leading-tight mb-2">
                  Cash Out to Bank
                </h2>
                <p className="text-[#90cb90]/70 text-sm mb-8">
                  Convert USDC to USD and withdraw to your bank
                </p>

                {/* Amount Input */}
                <div className="w-full mb-6">
                  <div className="flex justify-between items-end px-1 mb-2">
                    <label className="text-white text-sm font-bold">Amount (USDC)</label>
                    <span className="text-xs font-semibold text-[#90cb90] bg-white/5 px-2 py-0.5 rounded-md">
                      ${MIN_OFFRAMP_AMOUNT} - ${MAX_OFFRAMP_AMOUNT.toLocaleString()}
                    </span>
                  </div>
                  <div className="relative flex items-center w-full rounded-2xl bg-[#224922] border border-transparent focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/50 transition-all duration-300 overflow-hidden">
                    <div className="pl-5 text-white/40 text-3xl font-medium">$</div>
                    <input
                      type="number"
                      value={offrampAmount}
                      onChange={(e) => setOfframpAmount(e.target.value)}
                      placeholder="0.00"
                      min={MIN_OFFRAMP_AMOUNT}
                      max={Math.min(balance, MAX_OFFRAMP_AMOUNT)}
                      step="0.01"
                      className="w-full bg-transparent border-none text-white focus:ring-0 h-20 placeholder:text-white/10 p-2 pl-1 text-[40px] font-bold tracking-tight [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="pr-4">
                      <button
                        onClick={handleOfframpMax}
                        className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 hover:border-primary/40 text-xs font-extrabold px-4 py-2.5 rounded-xl transition-all tracking-wider active:scale-95"
                      >
                        MAX
                      </button>
                    </div>
                  </div>
                  {numericOfframpAmount > balance && (
                    <p className="text-red-400 text-xs mt-2 ml-1">Insufficient balance</p>
                  )}
                </div>

                {/* Quote Preview */}
                {offrampQuote && offrampState === "success" && (
                  <div className="w-full mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="text-white text-xs font-semibold mb-3 text-left">Fee Breakdown</p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-[#90cb90]/70">You'll receive</span>
                        <span className="text-white font-semibold">${offrampQuote.cashoutTotal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#90cb90]/70">Coinbase fee</span>
                        <span className="text-white">${offrampQuote.coinbaseFee}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {offrampState === "error" && offrampError && (
                  <div className="w-full mb-6 bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
                    {offrampError}
                  </div>
                )}

                {/* Info Banner */}
                {offrampState !== "polling" && (
                  <div className="w-full mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                    <span className="material-symbols-outlined text-blue-400 shrink-0 text-xl">info</span>
                    <div className="text-white/70 text-xs leading-relaxed text-left">
                      <p className="mb-1">1. Set up your bank account in Coinbase</p>
                      <p className="mb-1">2. Confirm the withdrawal amount</p>
                      <p>3. Funds arrive in 1-3 business days</p>
                    </div>
                  </div>
                )}

                {/* Polling Status Banner */}
                {offrampState === "polling" && (
                  <div className="w-full mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex gap-3 items-center">
                      <span className="material-symbols-outlined text-blue-400 animate-spin shrink-0 text-xl">sync</span>
                      <div className="text-white/90 text-sm leading-relaxed flex-1">
                        <p className="font-semibold">Waiting for withdrawal confirmation...</p>
                        <p className="text-xs text-white/60 mt-1">Complete your bank setup in the Coinbase window</p>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelOfframpPolling}
                      className="mt-3 w-full text-sm text-white/50 hover:text-white/70 transition-colors py-2"
                    >
                      Cancel and try again
                    </button>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={handleOfframp}
                  disabled={!isValidOfframpAmount || offrampState === "processing" || offrampState === "polling"}
                  className={`w-full group relative font-extrabold text-lg h-[60px] rounded-2xl transition-all active:scale-[0.98] overflow-hidden flex items-center justify-center gap-3 ${
                    isValidOfframpAmount && offrampState !== "processing" && offrampState !== "polling"
                      ? "bg-primary hover:bg-primary/90 text-[#102210] shadow-[0_0_25px_-5px_rgba(13,242,13,0.4)]"
                      : "bg-white/10 text-white/40 cursor-not-allowed"
                  }`}
                >
                  {offrampState === "processing" ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">sync</span>
                      <span>Generating link...</span>
                    </>
                  ) : offrampState === "polling" ? (
                    <>
                      <span className="material-symbols-outlined animate-pulse">hourglass_top</span>
                      <span>Waiting for confirmation...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue to Coinbase</span>
                      <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">
                        arrow_forward
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Step 2b: Wallet Transfer */}
            {step === "wallet-transfer" && (
              <div className="mt-8">
                {/* Header */}
                <div className="flex flex-col items-center text-center mb-8">
                  <div className="relative mb-4">
                    <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                    <div className="relative w-20 h-20 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10">
                      <span className="material-symbols-outlined text-primary text-[36px]">send</span>
                    </div>
                  </div>
                  <h2 className="text-white tracking-tight text-[24px] font-extrabold leading-tight mb-2">
                    Transfer to Wallet
                  </h2>
                  <div className="flex items-center gap-2.5 bg-[#224922]/40 px-4 py-1.5 rounded-full border border-white/5">
                    <span className="text-[#90cb90] text-sm font-semibold">
                      Available: <span className="text-white font-bold">${balance.toFixed(2)}</span>
                    </span>
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-6">
                  {/* Network Card */}
                  <div className="flex items-center justify-between bg-[#182e18] border border-white/5 rounded-2xl p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#0052FF] flex items-center justify-center shadow-lg ring-2 ring-[#0052FF]/20">
                        <span className="material-symbols-outlined text-white text-2xl">token</span>
                      </div>
                      <div className="flex flex-col text-left">
                        <span className="text-[#90cb90] text-[11px] font-bold uppercase tracking-wider mb-0.5">
                          Network
                        </span>
                        <span className="text-white font-bold text-lg">Base Mainnet</span>
                      </div>
                    </div>
                    <span
                      className="material-symbols-outlined text-primary"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      check_circle
                    </span>
                  </div>

                  {/* To Address Input */}
                  <div className="space-y-2.5">
                    <label className="block text-white text-sm font-bold ml-1">To Address</label>
                    <div className="group relative flex items-center w-full rounded-2xl bg-[#224922] border border-transparent focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/50 transition-all duration-300">
                      <div className="pl-4 pr-2 text-[#90cb90]">
                        <span className="material-symbols-outlined text-2xl group-focus-within:text-white transition-colors">
                          qr_code_scanner
                        </span>
                      </div>
                      <input
                        type="text"
                        value={toAddress}
                        onChange={(e) => setToAddress(e.target.value)}
                        placeholder="Paste Base Network Address"
                        className="w-full bg-transparent border-none text-white focus:ring-0 h-16 placeholder:text-[#90cb90]/50 p-2 text-[17px] font-medium truncate"
                      />
                    </div>
                    {toAddress && !isValidAddress && (
                      <p className="text-red-400 text-xs ml-1">Invalid address format</p>
                    )}
                  </div>

                  {/* Amount Input */}
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-end px-1">
                      <label className="text-white text-sm font-bold">Amount (USD)</label>
                      <span className="text-xs font-semibold text-[#90cb90] bg-white/5 px-2 py-0.5 rounded-md">
                        Min: ${MIN_WALLET_AMOUNT.toFixed(2)}
                      </span>
                    </div>
                    <div className="relative flex items-center w-full rounded-2xl bg-[#224922] border border-transparent focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/50 transition-all duration-300 overflow-hidden">
                      <div className="pl-5 text-white/40 text-3xl font-medium">$</div>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        min={MIN_WALLET_AMOUNT}
                        max={balance}
                        step="0.01"
                        className="w-full bg-transparent border-none text-white focus:ring-0 h-20 placeholder:text-white/10 p-2 pl-1 text-[40px] font-bold tracking-tight [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <div className="pr-4">
                        <button
                          onClick={handleMax}
                          className="bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 hover:border-primary/40 text-xs font-extrabold px-4 py-2.5 rounded-xl transition-all tracking-wider active:scale-95"
                        >
                          MAX
                        </button>
                      </div>
                    </div>
                    {numericAmount > balance && (
                      <p className="text-red-400 text-xs ml-1">Insufficient balance</p>
                    )}
                  </div>

                  {/* Warning Banner */}
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full shrink-0 ring-1 ring-amber-500/30 flex items-center justify-center">
                      <span
                        className="material-symbols-outlined text-amber-400 text-xl"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        warning
                      </span>
                    </div>
                    <div>
                      <h4 className="text-amber-400 text-xs font-bold uppercase tracking-wide mb-1">
                        Network Warning
                      </h4>
                      <p className="text-amber-200/90 text-xs leading-relaxed">
                        Ensure this is a Base Network address. Wrong network = permanent loss.
                      </p>
                    </div>
                  </div>

                  {/* Error Message */}
                  {withdrawState === "error" && errorMessage && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400 text-sm">
                      {errorMessage}
                    </div>
                  )}

                  {/* Success Message */}
                  {withdrawState === "success" && (
                    <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-primary text-sm flex items-center gap-2">
                      <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                        check_circle
                      </span>
                      Withdrawal successful! Closing...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer (only for wallet transfer step) */}
          {step === "wallet-transfer" && (
            <div className="shrink-0 p-6 pt-4 bg-[#102210] border-t border-white/5 z-30 relative">
              <button
                onClick={handleWithdraw}
                disabled={!canWithdraw || isLoading}
                className={`w-full group relative font-extrabold text-lg h-[60px] rounded-2xl transition-all active:scale-[0.98] overflow-hidden flex items-center justify-center gap-3 ${
                  canWithdraw && !isLoading
                    ? "bg-primary hover:bg-primary/90 text-[#102210] shadow-[0_0_25px_-5px_rgba(13,242,13,0.4)]"
                    : "bg-white/10 text-white/40 cursor-not-allowed"
                }`}
              >
                {isLoading || withdrawState === "processing" ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">sync</span>
                    <span>Processing...</span>
                  </>
                ) : withdrawState === "success" ? (
                  <>
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      check_circle
                    </span>
                    <span>Success!</span>
                  </>
                ) : (
                  <>
                    <span>Send Funds</span>
                    <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">
                      arrow_forward
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
